import { Button, Card, CardContent, CardHeader, CardTitle, ComplianceBadge, EmptyState, ErrorState, Skeleton } from '@sitewise/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ClipboardCheck, Paperclip } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { fetchComplianceRecord, type RecordWithDeviceType } from '../../../api/compliance.js';

import { formatDate, formatRelativeDays } from './lib.js';
import { LogComplianceSheet } from './log-sheet.js';
import { SkipSnoozeDialog } from './skip-snooze-dialog.js';

export function ComplianceRecordDetailPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const recordQuery = useQuery({
    queryKey: ['compliance', 'record', id],
    queryFn: () => fetchComplianceRecord(id),
    enabled: id.length > 0,
  });
  const [logOpen, setLogOpen] = useState(false);
  const [skipKind, setSkipKind] = useState<'skip' | 'snooze' | null>(null);

  if (!id) return <ErrorState description="Missing compliance record id." />;
  if (recordQuery.isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }
  if (recordQuery.isError || !recordQuery.data) {
    return <ErrorState description="Could not load this compliance record." />;
  }

  const row: RecordWithDeviceType = {
    ...recordQuery.data.record,
    device: recordQuery.data.device,
    deviceType: recordQuery.data.deviceType,
  };
  const isActionable =
    row.status === 'upcoming' || row.status === 'due_soon' || row.status === 'overdue';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div className="space-y-1">
          <Link to="/compliance/records" className="text-xs text-primary hover:underline">
            ← Back to compliance records
          </Link>
          <h1 className="text-xl font-semibold">{row.device.label}</h1>
          <p className="text-sm text-fg-muted">{row.deviceType.name}</p>
        </div>
        <ComplianceBadge tone={row.tone} withDot>
          {row.status.replace('_', ' ')}
        </ComplianceBadge>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Schedule</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 text-sm sm:grid-cols-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-fg-muted">Due</div>
            <div className="font-medium">{formatDate(row.dueAt)}</div>
            <div className="text-xs text-fg-muted">{formatRelativeDays(row.dueAt)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-fg-muted">Grace until</div>
            <div className="font-medium">{formatDate(row.graceUntilAt)}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-fg-muted">Linked work order</div>
            {row.autoWorkOrderId ? (
              <Link to={`/work-orders/${row.autoWorkOrderId}`} className="font-medium text-primary hover:underline">
                Open work order
              </Link>
            ) : (
              <span className="text-fg-muted">None</span>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Pending log attachments</CardTitle>
          {isActionable ? (
            <div className="flex gap-2">
              <Button size="sm" onClick={() => setLogOpen(true)}>
                Log
              </Button>
              <Button size="sm" variant="outline" onClick={() => setSkipKind('snooze')}>
                Snooze
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSkipKind('skip')}>
                Skip
              </Button>
            </div>
          ) : null}
        </CardHeader>
        <CardContent>
          {recordQuery.data.pendingAttachments.length === 0 ? (
            <EmptyState
              icon={ClipboardCheck}
              title="No pending attachments"
              description="Attachments appear here when a log has been started but not submitted."
            />
          ) : (
            <ul className="divide-y divide-border text-sm">
              {recordQuery.data.pendingAttachments.map((att) => (
                <li key={att.id} className="flex items-center justify-between gap-2 py-2">
                  <div className="flex min-w-0 items-center gap-2">
                    <Paperclip className="h-4 w-4 text-fg-muted" />
                    <span className="truncate">{att.originalFilename}</span>
                  </div>
                  {att.signedUrl ? (
                    <a href={att.signedUrl} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                      View
                    </a>
                  ) : (
                    <span className="text-xs text-fg-muted">No preview</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {logOpen ? (
        <LogComplianceSheet
          record={row}
          onClose={() => setLogOpen(false)}
          onSubmitted={() => {
            setLogOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
            navigate('/compliance/records');
          }}
        />
      ) : null}

      {skipKind ? (
        <SkipSnoozeDialog
          kind={skipKind}
          record={row}
          onClose={() => setSkipKind(null)}
          onSubmitted={() => {
            setSkipKind(null);
            void queryClient.invalidateQueries({ queryKey: ['compliance'] });
            navigate('/compliance/records');
          }}
        />
      ) : null}
    </div>
  );
}
